import { RegistrationProgress } from '@/components/ProgressBar/RegistrationProgress'
import api from '@/services/api'
import { notificationService } from '@/services/notificationService'
import useAccessStore from '@/store/accessStore'
import { ErrorHandler } from '@/utils/ErrorHandler'
import { API_ENDPOINTS } from '@/utils/apiEndpoints'
import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBrandUserStore from '../../store/brandUserStore'
import useRegistrationOptionsStore from '../../store/registrationOptionsStore'
import { RegistrationBusinessForm } from './RegistrationBusinessForm'
import styles from './registration.module.css'

export const RegistrationBusiness = () => {
  const navigate = useNavigate()
  const setValue = useBrandUserStore((s) => s.setValue)
  const { brandData } = useAccessStore()

  const { currencies, budgets, companySizes, initialize, isInitialized } = useRegistrationOptionsStore()

  const [values, setValues] = useState({
    companyName: '',
    jobPosition: '',
    currency: '',
    budget: '',
    companySize: '',
  })

  useEffect(() => {
    if (!isInitialized) {
      void initialize()
    }
  }, [initialize, isInitialized])

  // Load pre-filled data from backend
  useEffect(() => {
    if (brandData) {
      setValues((prev) => ({
        ...prev,
        companyName: (brandData.companyName as string) || '',
        jobPosition: (brandData.jobPosition as string) || '',
        currency: typeof brandData.annualBudgetCurrency === 'string' ? brandData.annualBudgetCurrency : '',
        budget: (brandData.annualBudget as string) || '',
        companySize: (brandData.companySize as string) || '',
      }))
    }
  }, [brandData])

  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    const sanitized =
      id === 'companyName' ? value.replace(/[^a-zA-ZăĂâÂîÎțȚșȘ\-'"_.,@&!?:0-9-]/g, '') : value.replace(/[^a-zA-ZăĂâÂîÎțȚșȘ -]/g, '')
    setValues((prev) => ({ ...prev, [id]: sanitized }))
    setValue(id, sanitized)
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }))
  }

  const handleSelectChange = (id: 'currency' | 'budget' | 'companySize', value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }))
    if (id === 'companySize') setValue('companySize', value)
    if (id === 'budget') {
      setValue('annualBudget', value)
    }
    if (id === 'currency') {
      setValue('annualBudgetCurrency', value)
    }
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }))
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!values.companyName.trim()) newErrors.companyName = 'Company name is required'
    if (!values.jobPosition.trim()) newErrors.jobPosition = 'Job position is required'
    if (!values.currency) newErrors.currency = 'Please select a currency'
    if (!values.budget) newErrors.budget = 'Please select a budget'
    if (!values.companySize) newErrors.companySize = 'Please select company size'
    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0]
      notificationService.error(firstError)
    }

    return Object.keys(newErrors).length === 0
  }

  const handleBack = async () => {
    await useAccessStore.getState().fetchBrandData()
    navigate('/registration/industries')
  }

  const handleNext = async () => {
    if (!validate()) return
    try {
      await api.post(API_ENDPOINTS.BRAND.REGISTER_STEP_TWO, {
        companyName: values.companyName,
        jobPosition: values.jobPosition,
        annualBudget: values.budget,
        annualBudgetCurrency: values.currency,
        companySize: values.companySize,
      })
      await useAccessStore.getState().fetchBrandData()
      navigate('/registration/upload-logo')
    } catch (error) {
      console.error('Failed to submit business step:', error)
      const axiosError = error as { response?: { data?: unknown } }
      const parsed = ErrorHandler.parseApiErrorResponse(axiosError.response?.data)
      if (parsed && Object.keys(parsed).length > 0) {
        setErrors((prev) => ({ ...prev, ...parsed }))

        const firstError = Object.values(parsed)[0]
        if (firstError) {
          notificationService.error(String(firstError))
        }
      }
    }
  }

  const currencyOptions = useMemo(() => currencies.map((c) => ({ value: c, label: c })), [currencies])
  const budgetOptions = useMemo(() => budgets.map((b) => ({ value: b, label: b })), [budgets])
  const companySizeOptions = useMemo(() => companySizes.map((s) => ({ value: s, label: s })), [companySizes])

  return (
    <>
      <div className={styles.mainContentWrapper}>
        <h1 className={styles.title}>Business</h1>
        <p className={styles.textGrey}>Provide key information about your company or organization.</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <RegistrationProgress />
        </div>

        <RegistrationBusinessForm
          values={values}
          // errors={errors}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
          onNext={handleNext}
          onBack={handleBack}
          currencyOptions={currencyOptions}
          budgetOptions={budgetOptions}
          companySizeOptions={companySizeOptions}
        />
      </div>
    </>
  )
}
